import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';

const MAX_POLLS = 30;
const POLL_INTERVAL_MS = 3000;

export default function PaymentCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { api, refreshUser } = useAuth();
  const [status, setStatus] = useState('verifying');
  const [credits, setCredits] = useState(0);
  const [bonusCredits, setBonusCredits] = useState(0);
  const [productName, setProductName] = useState('');
  const [message, setMessage] = useState('');
  const pollCount = useRef(0);
  const pollTimer = useRef(null);

  const reference = searchParams.get('reference') || searchParams.get('trxref');
  const isProduct = searchParams.get('type') === 'product';
  const verifyPath = isProduct
    ? `/payments/flutterwave/product/verify/${reference}`
    : `/payments/flutterwave/verify/${reference}`;

  useEffect(() => {
    if (reference) {
      verifyPayment();
    } else {
      setStatus('error');
      setMessage('No payment reference found.');
    }
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
  }, [reference, isProduct]);

  const handleSuccess = async (data) => {
    setStatus('success');
    if (isProduct) {
      setProductName(data.product_name || 'your product');
      toast.success('Purchase complete!');
    } else {
      setCredits(data.credits);
      setBonusCredits(data.bonus_credits || 0);
      await refreshUser();
      if (data.bonus_credits > 0) {
        toast.success(`Credits added — plus ${data.bonus_credits} bonus credits for your first purchase!`);
      } else {
        toast.success('Credits added successfully!');
      }
    }
  };

  const verifyPayment = async () => {
    try {
      const response = await api.get(verifyPath);
      const { status: payStatus, message: payMsg } = response.data;

      if (payStatus === 'success') {
        await handleSuccess(response.data);
      } else if (payStatus === 'failed') {
        setStatus('error');
        setMessage(payMsg || 'Payment failed — try another card.');
      } else {
        setStatus('pending');
        setMessage(payMsg || 'Awaiting payment confirmation...');
        schedulePoll();
      }
    } catch {
      setStatus('error');
      setMessage(
        isProduct
          ? 'Payment verification failed. If you completed payment, your purchase will appear in My Library automatically.'
          : 'Payment verification failed. If you completed payment, your credits will appear automatically.',
      );
    }
  };

  const schedulePoll = () => {
    if (pollCount.current >= MAX_POLLS) {
      setMessage(
        isProduct
          ? 'Still waiting for confirmation. Your purchase will appear in My Library once payment is confirmed.'
          : 'Still waiting for confirmation. Your credits will be added automatically once payment is confirmed. You can safely leave this page.',
      );
      return;
    }
    pollTimer.current = setTimeout(async () => {
      pollCount.current += 1;
      try {
        const response = await api.get(verifyPath);
        if (response.data.status === 'success') {
          await handleSuccess(response.data);
          return;
        }
        if (response.data.status === 'failed') {
          setStatus('error');
          setMessage(response.data.message || 'Payment failed — try another card.');
          return;
        }
        schedulePoll();
      } catch {
        schedulePoll();
      }
    }, POLL_INTERVAL_MS);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-b from-primary/5 to-background">
      <Card className="w-full max-w-md card-elevated" data-testid="payment-callback-card">
        <CardContent className="pt-10 pb-10 text-center space-y-8">
          {status === 'verifying' && (
            <>
              <div className="h-20 w-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-foreground" data-testid="payment-status-title">Verifying Payment</h1>
                <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="h-20 w-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-primary" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-foreground" data-testid="payment-status-title">
                  {isProduct ? 'Purchase Successful' : 'Credits Added Successfully'}
                </h1>
                <p className="text-muted-foreground">
                  {isProduct
                    ? `${productName} has been added to your library.`
                    : `${credits} credits have been added to your account.`}
                </p>
                {!isProduct && bonusCredits > 0 && (
                  <div
                    data-testid="payment-bonus-banner"
                    className="mt-3 mx-auto inline-flex items-center gap-2 rounded-full bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-700 ring-1 ring-orange-200"
                  >
                    +{bonusCredits} bonus credits added — first purchase reward
                  </div>
                )}
              </div>
              <Button
                className="w-full glow-button font-medium"
                onClick={() => navigate(isProduct ? '/my-library?purchased=1' : '/dashboard')}
                data-testid="payment-success-continue-btn"
              >
                {isProduct ? 'Go to My Library' : 'Continue to Dashboard'}
              </Button>
            </>
          )}

          {status === 'pending' && (
            <>
              <div className="h-20 w-20 mx-auto rounded-full bg-amber-50 flex items-center justify-center">
                <Clock className="h-10 w-10 text-amber-500 animate-pulse" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-foreground" data-testid="payment-status-title">Pending Payment Confirmation</h1>
                <p className="text-muted-foreground">{message}</p>
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground pt-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>Checking automatically...</span>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full font-medium"
                onClick={() => navigate(isProduct ? '/my-library' : '/dashboard')}
                data-testid="go-to-dashboard-pending-btn"
              >
                {isProduct ? 'Go to My Library' : 'Continue to Dashboard'}
              </Button>
              {!isProduct && (
                <p className="text-xs text-muted-foreground">
                  Credits will be added automatically once your payment is confirmed. You can safely leave this page.
                </p>
              )}
            </>
          )}

          {status === 'error' && (
            <>
              <div className="h-20 w-20 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
                <XCircle className="h-10 w-10 text-destructive" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-foreground" data-testid="payment-status-title">Payment Failed</h1>
                <p className="text-muted-foreground">{message || 'Payment failed — try another card.'}</p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 font-medium"
                  onClick={() => navigate(isProduct ? '/templates' : '/credits')}
                  data-testid="try-again-btn"
                >
                  Try Again
                </Button>
                <Button
                  className="flex-1 font-medium"
                  onClick={() => navigate(isProduct ? '/my-library' : '/dashboard')}
                  data-testid="go-to-dashboard-error-btn"
                >
                  {isProduct ? 'My Library' : 'Dashboard'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
